import { Injectable, signal } from '@angular/core';

const VOLUME_KEY = 'tf2_volume';

interface SoundFile {
  name: string;
  format: string;
}

@Injectable({
  providedIn: 'root',
})
export class SoundsService {
  private readonly SOUNDS: SoundFile[] = [
    { name: 'big-kill', format: 'mp3' },
    { name: 'closer', format: 'mp3' },
    { name: 'enforcer', format: 'mp3' },
    { name: 'explosion', format: 'mp3' },
    { name: 'family-business', format: 'mp3' },
    { name: 'kukri', format: 'mp3' },
    { name: 'golden-pan', format: 'mp3' },
    { name: 'lightning', format: 'wav' },
    { name: 'lugermorph', format: 'mp3' },
    { name: 'minigun', format: 'mp3' },
    { name: 'panic-attack', format: 'mp3' },
    { name: 'plinko-high', format: 'mp3' },
    { name: 'plinko-low', format: 'mp3' },
    { name: 'plinko-med', format: 'mp3' },
    { name: 'rocket-launcher', format: 'mp3' },
    { name: 'saxxy', format: 'mp3' },
    { name: 'scattergun', format: 'mp3' },
    { name: 'shotgun', format: 'mp3' },
    { name: 'tick', format: 'mp3' },
  ];
  private readonly soundsStore = new Map<string, HTMLAudioElement>();

  public volume = signal<number>(1);

  constructor() {
    const volume = localStorage.getItem(VOLUME_KEY);
    if (volume) {
      this.volume.set(parseFloat(volume));
    }

    this.preloadSounds();
  }

  async preloadSounds() {
    const isRequestIdleCallbackSupported = 'requestIdleCallback' in window;

    if (isRequestIdleCallbackSupported) {
      requestIdleCallback(() => {
        this.preloadAllSounds();
      });
    } else {
      this.preloadAllSounds();
    }
  }

  private preloadAllSounds() {
    this.SOUNDS.forEach((sound) => {
      try {
        this.loadAudio(sound);
      } catch (error) {
        console.log(
          `🚀 ~ SoundsService preloadAllSounds ~ ${sound.name}.${sound.format} ~ error:`,
          error
        );
      }
    });
  }

  playSound(
    sound: SoundFile['name'],
    options?: {
      extension?: SoundFile['format'];
      maxVolume?: number;
    }
  ) {
    const maxVolume = options?.maxVolume ?? 1;

    if (this.volume() <= 0 || maxVolume <= 0) {
      return;
    }

    try {
      const extension = options?.extension ?? 'mp3';
      const soundFullName = this.getSoundKey({
        name: sound,
        format: extension,
      });

      const preloadedAudio = this.soundsStore.get(soundFullName);
      const audio = preloadedAudio
        ? (preloadedAudio.cloneNode() as HTMLAudioElement)
        : this.loadAudio({ name: sound, format: extension });

      this.playAudio(audio, Math.min(this.volume(), maxVolume));
    } catch (error) {
      console.log('🚀 ~ SoundsService playSound ~ error:', error);
    }
  }

  public loadAudio(sound: SoundFile) {
    const soundFullName = this.getSoundKey(sound);
    const soundsPath = 'assets/sounds';

    const audio = new Audio();
    audio.src = `${soundsPath}/${soundFullName}`;
    audio.preload = 'auto';
    audio.load();

    this.soundsStore.set(soundFullName, audio);

    return audio;
  }

  public setVolume(volume: number) {
    this.volume.set(volume);
    localStorage.setItem(VOLUME_KEY, this.volume().toString());
  }

  public getSoundKey(sound: SoundFile) {
    const soundFullName = `${sound.name}.${sound.format}`;
    return soundFullName;
  }

  private playAudio(audio: HTMLAudioElement, volume: number) {
    audio.volume = volume;
    audio.play().catch((error) => {
      console.log('🚀 ~ SoundsService playAudio ~ error:', error);
    });
  }
}
